import React from 'react';
import { Check, X } from 'lucide-react';
import { REGISTER_URL } from '../lib/authLinks';

const PricingPlansSection = () => {
  const plans = [
    {
      name: 'BASIC PLAN',
      icon: '⭐',
      minDeposit: '$100',
      color: 'border-gray-500',
      buttonColor: 'bg-gray-700 hover:bg-gray-600 text-white',
      features: [
        { text: 'AI Auto Trading Enabled', included: true },
        { text: 'Real-Time Market Execution (Stock & Forex)', included: true },
        { text: 'Zero Manual Work', included: true },
        { text: 'Risk-Managed Trading', included: true },
        { text: 'Standard Support', included: true },
        { text: 'Bonus Not Included', included: false }
      ]
    },
    {
      name: 'SILVER PLAN',
      icon: '💠',
      minDeposit: '$500',
      color: 'border-cyan-400',
      buttonColor: 'bg-cyan-500 hover:bg-cyan-600 text-white',
      features: [
        { text: 'Advanced AI Trading Strategies', included: true },
        { text: 'Faster Trade Execution', included: true },
        { text: 'Daily Profit Summary', included: true },
        { text: 'Personal Account Support', included: true },
        { text: 'Risk-Managed Trading', included: true },
        { text: 'Bonus Not Included', included: false }
      ]
    },
    {
      name: 'GOLD PLAN',
      icon: '🟡',
      minDeposit: '$2,000',
      color: 'border-yellow-500',
      buttonColor: 'bg-yellow-500 hover:bg-yellow-600 text-black',
      highlighted: true,
      features: [
        { text: 'Multi-Strategy AI Trading', included: true },
        { text: 'Higher Profitability Settings', included: true },
        { text: 'Priority Executions', included: true },
        { text: 'Dedicated Account Manager', included: true },
        { text: 'Capital Safety Enabled', included: true },
        { text: 'Weekly Profit Optimization Review', included: true },
        { text: 'Bonus Included', included: true }
      ]
    },
    {
      name: 'PLATINUM PLAN (VIP)',
      icon: '🔷',
      minDeposit: '$5,000',
      color: 'border-blue-400',
      buttonColor: 'bg-blue-500 hover:bg-blue-600 text-white',
      features: [
        { text: 'Advanced Multi-AI Strategy', included: true },
        { text: 'Highest Profit Potential', included: true },
        { text: 'Senior Account Manager', included: true },
        { text: 'Full Capital Safety & Auto Risk Control', included: true },
        { text: 'Profit Management Services', included: true },
        { text: 'Priority Withdrawals', included: true },
        { text: 'Exclusive Bonus Reward', included: true }
      ]
    }
  ];

  return (
    <section id="pricing-plans" className="relative py-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Start Trading with <span className="text-yellow-500">SpreadexAsia</span>
          </h2>
          <p className="text-gray-400 text-lg">Choose the plan that fits your trading goals</p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-2xl p-6 border-2 ${plan.color} ${
                plan.highlighted ? 'transform scale-105 shadow-2xl shadow-yellow-500/20' : ''
              } bg-gray-900/50 backdrop-blur-sm hover:transform hover:scale-105 transition-all duration-300 flex flex-col`}
            >
              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className="text-4xl mb-3">{plan.icon}</div>
                <h3 className="text-xl font-bold mb-2 uppercase tracking-wide">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-gray-400 text-sm">Min Deposit: </span>
                  <span className="text-2xl font-bold text-blue-400">{plan.minDeposit}</span>
                </div>
              </div>

              {/* Features List */}
              <div className="space-y-3 mb-6 flex-grow">
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${feature.included ? 'text-gray-300' : 'text-gray-500'}`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" className="mt-auto">
                <button
                  className={`w-full ${plan.buttonColor} font-bold py-3 px-6 rounded-xl transition-all duration-300 hover:shadow-lg flex items-center justify-center space-x-2`}
                >
                  <span>Get Started</span>
                  <span>→</span>
                </button>
              </a>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="text-center mt-12">
          <p className="text-gray-400 text-sm">
            All plans include secure deposits, fast withdrawals, and 24/7 customer support
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingPlansSection;
